class QuizContainer extends HTMLElement {
  constructor() {
    super();
    this.currentQuestionId = 'q1';
    this.answers = {};
    this.quizData = null;
    this.history = [];
  }

  async connectedCallback() {
    this.modal            = this.querySelector('[data-quiz-modal]');
    this.questionEl       = this.querySelector('[data-question]');
    this.descriptionEl    = this.querySelector('[data-description]');
    this.optionsContainer = this.querySelector('[data-options]');
    this.progressFill     = this.querySelector('[data-progress-fill]');
    this.stepIndicator    = this.querySelector('[data-step-indicator]');
    this.nextBtn          = this.querySelector('[data-quiz-next]');
    this.backBtn          = this.querySelector('[data-quiz-back]');
    this.navigation       = this.querySelector('.quiz-navigation');
    this.quizContent      = this.querySelector('.quiz-content');
    this.closeBtn         = this.querySelector('[data-quiz-close]');
    this.triggers         = this.querySelectorAll('[data-quiz-trigger]');

    this.setupEvents();
    await this.loadQuizData();

    if (!this.quizData) {
      console.error('Quiz data missing');
      return;
    }

    this.quiz       = this.quizData.quiz;
    this.totalSteps = this.quiz.totalSteps || 5;
    this.renderQuestion(this.currentQuestionId);
  }

  async loadQuizData() {
    try {
      const jsonEl = document.getElementById('quiz-data');
      if (!jsonEl) throw new Error('Quiz data element not found');
      this.quizData = JSON.parse(jsonEl.textContent);
    } catch (err) {
      console.error('Quiz data failed to load:', err);
    }
  }

  setupEvents() {
    this.triggers.forEach(t => t.addEventListener('click', () => this.openModal()));
    this.closeBtn.addEventListener('click', () => this.closeModal());
    this.modal.addEventListener('click', e => {
      if (e.target === this.modal) this.closeModal();
    });
    this.nextBtn.addEventListener('click', () => this.handleNext());
    this.backBtn.addEventListener('click', () => this.handleBack());
    this.addEventListener('click', (event) => { const restartButton = event.target.closest('[data-quiz-restart]');
      if (!restartButton) return;
      this.resetQuiz();
    });
  }

  openModal() {
    this.modal.classList.add('active');
    document.body.style.overflow = 'hidden';
  }

  closeModal() {
    this.modal.classList.remove('active');
    document.body.style.overflow = '';
  }

  /* -------------------------------------------------- */
  /*  Question rendering                                 */
  /* -------------------------------------------------- */
  renderQuestion(questionId) {
    const question = this.quiz.questions.find(q => q.id === questionId);
    if (!question) return;

    if (question.type === 'branch') {
      this.handleBranch(question);
      return;
    }

    this._comingFromHandleNext = false;

    this.currentQuestion   = question;
    this.currentQuestionId = question.id;
    this.selectedOption    = null;
    this.nextBtn.disabled  = true;

    this.questionEl.textContent    = question.text;
    this.descriptionEl.textContent = this.quiz.description;

    this.optionsContainer.innerHTML = question.options
      .map(({ id, label }) => `
        <div class="quiz-option-card" data-option-id="${id}">
          <div class="quiz-card-container">
            <h4 class="quiz-option-title">${label}</h4>
          </div>
          <div class="quiz-option-selector"></div>
          <div class="quiz-checkmark">✓</div>
        </div>
      `)
      .join('');

    this.attachOptionEvents();
    this.updateProgress();
  }

  attachOptionEvents() {
    const cards = this.querySelectorAll('[data-option-id]');
    cards.forEach(card => {
      card.addEventListener('click', () => {
        cards.forEach(c => c.classList.remove('selected'));
        card.classList.add('selected');
        this.selectedOption = this.currentQuestion.options.find(
          o => o.id === card.dataset.optionId
        );
        this.nextBtn.disabled = false;
      });
    });
  }

  /* -------------------------------------------------- */
  /*  Navigation                                         */
  /* -------------------------------------------------- */
  handleNext() {
    if (!this.selectedOption) return;
    this.answers[this.currentQuestion.id] = this.selectedOption.id;
    this.history.push(this.currentQuestion.id);
    // Tell renderQuestion this call came from handleNext so it skips
    // the branch-route history push (we already pushed above).
    this._comingFromHandleNext = true;

    if (this.selectedOption.result) return this.showResult(this.selectedOption.result);
    if (this.selectedOption.next)   return this.renderQuestion(this.selectedOption.next);
  }

  handleBack() {
    if (this.history.length === 0) return;

    // Pop BEFORE calling renderQuestion so that when updateProgress() runs
    // inside renderQuestion, history already reflects the restored state.
    const previousQuestionId = this.history.pop();

    // Clear the answer for the question we're leaving (still currentQuestionId here).
    delete this.answers[this.currentQuestionId];

    this.renderQuestion(previousQuestionId);
  }

  handleBranch(question) {
    // Branch nodes are never pushed to history — they are invisible routing
    // steps. This keeps history.length accurate for the progress stepper.
    // IMPORTANT: when renderQuestion routes here, currentQuestion is NOT
    // updated, so the last real visible question stays as currentQuestion.
    // That means if the user is sitting on a post-branch visible question
    // (e.g. q4c_branch) and hits Back, history correctly pops the question
    // BEFORE the branch (e.g. q4), and answers["q4"] is cleared so the
    // branch re-evaluates cleanly when they pick again.
    for (const rule of question.rules) {
      const matched = Object.entries(rule.condition).every(([key, expected]) => {
        const actual = this.answers[key];
        return Array.isArray(expected) ? expected.includes(actual) : expected === actual;
      });

      if (matched) {
        if (rule.result) return this.showResult(rule.result);
        if (rule.next)   return this.renderQuestion(rule.next);
      }
    }
  }

  /* -------------------------------------------------- */
  /*  Helpers                                            */
  /* -------------------------------------------------- */
  formatPrice(price) {
    const locale   = (typeof Shopify !== 'undefined' && Shopify.locale) || 'de-DE';
    const currency = (typeof Shopify !== 'undefined' && Shopify.currency?.active) || 'EUR';
    return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(price / 100);
  }

  async fetchLiveAvailability(variants) {
    const results = await Promise.allSettled(
      variants.map(v => fetch(`/variants/${v.id}.js`).then(r => (r.ok ? r.json() : null)))
    );

    return variants.map((v, i) => {
      const live = results[i].status === 'fulfilled' ? results[i].value : null;
      if (!live) return { ...v, available: false };
      const purchasable = live.available === true || live.inventory_policy === 'continue';
      return { ...v, available: purchasable, _live: live };
    });
  }

  isSoldOutError(error) {
    const text = (error?.description || error?.message || '').toLowerCase();
    return text.includes('sold out') || text.includes('unavailable');
  }

  /* -------------------------------------------------- */
  /*  Notify form — back-in-stock email fallback         */
  /* -------------------------------------------------- */
  /**
   * Renders a "notify me" email signup form inside the result card.
   * Called when every variant of a recommended product is unavailable.
   *
   * @param {object} result        - quiz result config object
   * @param {object} product       - Shopify product JSON
   * @param {string} productImage  - image src for display
   * @param {string} productUrl    - /products/handle URL
   */
  renderNotifyForm(result, product, productImage, productUrl) {
    const ui = this.quiz.ui;

    this.optionsContainer.innerHTML = `
      <div class="quiz-result-card quiz-result-card--soldout">
        ${productImage
          ? `<div class="quiz-result-image-wrapper quiz-result-image-wrapper--soldout">
               <img src="${productImage}" alt="${result.title}" class="quiz-result-image" loading="lazy">
               <div class="quiz-soldout-overlay">
                 <span class="quiz-soldout-label">${ui.sold_out}</span>
               </div>
             </div>`
          : ''}
      </div>`;

    this.quizContent.classList.add('quiz-content--result');

    this.questionEl.innerHTML = `
      ${result.badge ? `<div class="quiz-result-badge quiz-result-badge--soldout" data-badge="${result.badge}">${result.badge}</div>` : ''}
      <br><br>${result.title}
    `;

    this.descriptionEl.innerHTML = `
      <div class="quiz-result-description">${result.description}</div>

      <div class="quiz-notify-block">
        <p class="quiz-notify-title">${ui.notify_title}</p>
        <p class="quiz-notify-text">${ui.notify_text}</p>

        <a href="https://kuvings.ch/en/pages/contact-us?product=${encodeURIComponent(product.handle)}" class="btn quiz-notify-btn">
          ${ui.notify_button}
        </a>
      </div>

      ${result.best_for?.length ? `
        <div class="quiz-result-group">
          <div class="quiz-result-group-title">
            ${ui.best_for}
          </div>
          <div class="quiz-result-tags">
            ${result.best_for.map(item =>
              `<span class="quiz-result-tag">${item}</span>`
            ).join('')}
          </div>
        </div>
      ` : ''}
      <div class="quiz-result-actions">
        <a
          href="${productUrl}"
          class="quiz-result-button"
        >
          ${ui.discover_now}
        </a>
        <button
          type="button"
          class="quiz-restart-btn"
          data-quiz-restart
        >
          ${ui.restart ?? 'Start over'}
        </button>
      </div>
    `;

  }

  /* -------------------------------------------------- */
  /*  Result rendering & cart                            */
  /* -------------------------------------------------- */
  updateVariantUI(variant) {
    this.optionsContainer.querySelectorAll('[data-variant-id]')
      .forEach(btn => btn.classList.remove('active'));

    this.optionsContainer
      .querySelector(`[data-variant-id="${variant.id}"]`)
      ?.classList.add('active');

    const imageEl = this.querySelector('.quiz-result-image');
    const newSrc  = variant.featured_media?.preview_image?.src;
    if (imageEl && newSrc) imageEl.src = newSrc;

    const priceEl = this.querySelector('.quiz-result-price');
    if (priceEl) priceEl.textContent = this.formatPrice(variant.price);

    const variantInput = this.querySelector('[data-variant-input]');
    if (variantInput) variantInput.value = variant.id;

    const addToCartBtn = this.querySelector('[data-add-to-cart]');
    if (addToCartBtn) {
      addToCartBtn.disabled  = !variant.available;
      addToCartBtn.innerHTML = variant.available ? this.quiz.ui.add_to_cart : this.quiz.ui.sold_out;
      addToCartBtn.classList.toggle('is-sold-out', !variant.available);
    }
  }

  markVariantSoldOut(variant) {
    variant.available = false;
    this.querySelector(`[data-variant-id="${variant.id}"]`)
      ?.setAttribute('disabled', 'disabled');
    this.querySelector(`[data-variant-id="${variant.id}"]`)
      ?.classList.add('is-disabled');
    this.updateVariantUI(variant);

    // If every variant is now sold out, swap the whole result panel to notify mode.
    const allVariants = Array.from(this.querySelectorAll('[data-variant-id]'));
    const allDisabled = allVariants.every(btn => btn.hasAttribute('disabled'));
    if (allDisabled) {
      this._switchToNotifyMode();
    }
  }

  /**
   * Replace the add-to-cart section with the notify form in-place,
   * preserving the product image and title that are already rendered.
   */
  _switchToNotifyMode() {
    const ui            = this.quiz.ui;
    const cartActions   = this.querySelector('.quiz-cart-actions');
    const variantPicker = this.querySelector('.quiz-variant-picker');
    const priceEl       = this.querySelector('.quiz-result-price');

    if (cartActions) {
      cartActions.innerHTML = `
        <div class="quiz-notify-block">
          <p class="quiz-notify-title">${ui.notify_title}</p>
          <p class="quiz-notify-text">${ui.notify_text}</p>
          <form class="quiz-notify-form" data-notify-form
                data-product-id="${this._currentProduct?.id || ''}"
                data-product-handle="${this._currentProduct?.handle || ''}"
                data-product-title="${this._currentResult?.title || ''}">
            <div class="quiz-notify-input-row">
              <input
                type="email"
                class="quiz-notify-email"
                data-notify-email
                placeholder="${ui.notify_placeholder}"
                required
                autocomplete="email"
              >
              <button type="submit" class="btn quiz-notify-btn" data-notify-btn>
                ${ui.notify_button}
              </button>
            </div>
            <p class="quiz-notify-success" data-notify-success hidden>${ui.notify_success}</p>
            <p class="quiz-notify-error"   data-notify-error   hidden></p>
          </form>
        </div>`;
    }

    variantPicker?.remove();
    priceEl?.remove();
  }

  async showResult(resultId) {
    // results is an array in the JSON, so find by id.
    const result = Array.isArray(this.quiz.results)
      ? this.quiz.results.find(r => r.id === resultId)
      : this.quiz.results[resultId];
    if (!result) return;

    // Fetch live product data; fall back to static data baked into the result JSON
    // if the store fetch fails (e.g. product deleted or network error).
    let product;
    try {
      const productResp = await fetch(`/products/${result.product_handle}.js`, { cache: 'no-store' });
      if (!productResp.ok) throw new Error(`HTTP ${productResp.status}`);
      product = await productResp.json();
      product.variants = await this.fetchLiveAvailability(product.variants);
    } catch (err) {
      console.warn('Product fetch failed, using fallback data:', err);
      const fallbackVariants = result.variants || [{
        id: result.default_variant || 0,
        title: 'Default',
        available: false,
        price: 0,
        featured_image: null,
      }];
      product = {
        id: 0,
        handle: result.product_handle,
        featured_image: result.fallback_image || null,
        variants: fallbackVariants,
      };
    }

    // Cache on instance for _switchToNotifyMode().
    this._currentProduct = product;
    this._currentResult  = result;

    const allSoldOut    = product.variants.every(v => !v.available);
    let selectedVariant = product.variants.find(v => v.available) || product.variants[0];
    // Use variant image → product image → fallback_image from result JSON.
    const productImage  = selectedVariant?.featured_image?.src
      || selectedVariant?.featured_image
      || product.featured_image
      || result.fallback_image
      || null;
    const productUrl    = result.url || `/products/${product.handle}`;

    /* ---- Shared result-screen finalization ---- */
    // Called by both the sold-out and normal paths below.
    const finalizeResultUI = () => {
      if (this.navigation) {
        this.navigation.style.display = 'none';
      }
      this.progressFill.style.width = '100%';
      this.stepIndicator.textContent =
        this.quiz.ui.recommendation_ready;
    };

    /* ---- Every variant unavailable → show notify form only ---- */
    if (allSoldOut) {
      this.renderNotifyForm(result, product, productImage, productUrl);
      finalizeResultUI();
      return;
    }

    /* ---- Normal result flow ---- */
    const variantMarkup = product.variants
      .map(v => `
        <button
          class="quiz-variant-option${v.available ? '' : ' is-disabled'}"
          data-variant-id="${v.id}"
          ${!v.available ? 'disabled' : ''}
        >${v.title}</button>`)
      .join('');

    this.optionsContainer.innerHTML = `
      <div class="quiz-result-card">
        ${productImage
          ? `<div class="quiz-result-image-wrapper">
               <img src="${productImage}" alt="${result.title}" class="quiz-result-image" loading="lazy">
             </div>`
          : ''}
      </div>`;

    this.quizContent.classList.add('quiz-content--result');

    this.questionEl.innerHTML = `
      ${result.badge ? `<div class="quiz-result-badge" data-badge="${result.badge}">${result.badge}</div>` : ''}
      <br><br>${result.title}
    `;

    this.descriptionEl.innerHTML = `
      <div class="quiz-result-description">${result.description}</div>
      <div class="quiz-variant-picker">${variantMarkup}</div>
      <div class="quiz-result-price">${selectedVariant.price ? this.formatPrice(selectedVariant.price) : (result.fallback_price || '')}</div>

      <div class="quiz-cart-actions">
        <form method="post" action="/cart/add" class="quiz-native-form">
          <input type="hidden" name="id" value="${selectedVariant.id}" data-variant-input>
          <div class="quiz-qty">
            <button type="button" data-qty-minus>−</button>
            <input type="number" name="quantity" value="1" min="1" class="quiz-qty-input">
            <button type="button" data-qty-plus>+</button>
          </div>
          <button
            type="submit"
            class="btn btn--full add-to-cart quiz-add-to-cart"
            data-add-to-cart
            ${!selectedVariant.available ? 'disabled' : ''}
          >${selectedVariant.available ? this.quiz.ui.add_to_cart : this.quiz.ui.sold_out}</button>
        </form>
      </div>

      ${result.recommendation_reason ? `<div class="quiz-result-reason">${result.recommendation_reason}</div>` : ''}
      ${result.best_for?.length ? `
        <div class="quiz-result-group">
          <div class="quiz-result-group-title">
            ${this.quiz.ui.best_for}
          </div>
          <div class="quiz-result-tags">
            ${result.best_for.map(item =>
              `<span class="quiz-result-tag">${item}</span>`
            ).join('')}
          </div>
        </div>
      ` : ''}
      <div class="quiz-result-actions">
        <a
          href="${productUrl}"
          class="quiz-result-button"
        >
          ${this.quiz.ui.discover_now}
        </a>
        <button
          type="button"
          class="quiz-restart-btn"
          data-quiz-restart
        >
          ${this.quiz.ui.restart ?? 'Start over'}
        </button>
      </div>
    `;

    // Quantity controls.
    const qtyInput = this.querySelector('.quiz-qty-input');
    this.querySelector('[data-qty-minus]')?.addEventListener('click', () => {
      const v = parseInt(qtyInput.value, 10);
      if (v > 1) qtyInput.value = v - 1;
    });
    this.querySelector('[data-qty-plus]')?.addEventListener('click', () => {
      qtyInput.value = parseInt(qtyInput.value, 10) + 1;
    });

    // Variant switching.
    this.querySelector('.quiz-variant-picker')?.addEventListener('click', e => {
      const btn = e.target.closest('[data-variant-id]');
      if (!btn) return;
      const variant = product.variants.find(v => v.id === Number(btn.dataset.variantId));
      if (!variant) return;
      selectedVariant = variant;
      this.updateVariantUI(variant);
    });

    this.updateVariantUI(selectedVariant);

    // Cart form submission.
    const nativeForm   = this.querySelector('.quiz-native-form');
    const addToCartBtn = this.querySelector('[data-add-to-cart]');

    nativeForm?.addEventListener('submit', async (e) => {
      e.preventDefault();
      if (!selectedVariant?.available) return;

      const latestVariant = await fetch(`/variants/${selectedVariant.id}.js`)
        .then(r => r.json())
        .catch(() => null);

      const stillAvailable =
        latestVariant &&
        (latestVariant.available === true || latestVariant.inventory_policy === 'continue');

      if (!stillAvailable) {
        this.markVariantSoldOut(selectedVariant);
        return;
      }

      addToCartBtn.disabled  = true;
      addToCartBtn.innerHTML = this.quiz.ui.adding;

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
            'Accept': 'application/json',
          },
          body: JSON.stringify({ id: selectedVariant.id, quantity: parseInt(qtyInput.value) || 1 }),
        });

        if (!response.ok) {
          const error = await response.json();

          if (this.isSoldOutError(error)) {
            this.markVariantSoldOut(selectedVariant);
            return;
          }

          addToCartBtn.disabled  = false;
          addToCartBtn.innerHTML = this.quiz.ui.try_again;
          return;
        }

        document.dispatchEvent(new CustomEvent('cart:build'));
        addToCartBtn.innerHTML = this.quiz.ui.redirecting_to_cart;
        setTimeout(() => { window.location.href = '/cart'; }, 400);

      } catch (err) {
        console.error('Cart error:', err);
        addToCartBtn.disabled  = false;
        addToCartBtn.innerHTML = this.quiz.ui.try_again;
      }
    });

    finalizeResultUI();
  }

  /* -------------------------------------------------- */
  /*  Progress bar                                       */
  /* -------------------------------------------------- */
  updateProgress() {
    const currentStep = this.getVisibleQuestionIndex(this.currentQuestionId);

    // Pass the real current question and collected answers so the
    // simulation can follow the actual branch path going forward.
    const totalSteps = this.calculateDynamicTotalSteps(
      this.currentQuestionId,
      this.answers
    );

    const progress = (currentStep / totalSteps) * 100;
    this.progressFill.style.width  = `${Math.min(progress, 100)}%`;
    this.stepIndicator.textContent = `${this.quiz.ui.step} ${currentStep} ${this.quiz.ui.of} ${totalSteps}`;
    this.backBtn.disabled          = currentStep <= 1;
  }

  getVisibleQuestionIndex(questionId) {
    const visibleHistory = [...this.history, questionId];
    const uniqueVisible  = visibleHistory.filter((id, index, arr) => {
      const q = this.quiz.questions.find(q => q.id === id);
      return q && q.type !== 'branch' && arr.indexOf(id) === index;
    });
    return uniqueVisible.length;
  }

  calculateDynamicTotalSteps(questionId, answers = {}) {
    // Seed with how many visible steps have already been seen (including current).
    let steps     = this.getVisibleQuestionIndex(questionId);
    let currentId = questionId;
    const visited = new Set();

    while (currentId && !visited.has(currentId)) {
      visited.add(currentId);

      const question = this.quiz.questions.find(q => q.id === currentId);
      if (!question) break;

      if (question.type === 'branch') {
        // Follow whichever branch rule matches the answers collected so far.
        const matchedRule = question.rules.find(rule =>
          Object.entries(rule.condition).every(([key, expected]) => {
            const actual = answers[key];
            return Array.isArray(expected) ? expected.includes(actual) : expected === actual;
          })
        );
        // Branch nodes don't add a visible step; just follow the route.
        if (!matchedRule || matchedRule.result) break;
        currentId = matchedRule.next;
        continue;
      }

      const selectedAnswerId = answers[question.id];

      if (selectedAnswerId) {
        // Follow the path the user actually chose.
        const selectedOption = question.options.find(o => o.id === selectedAnswerId);
        if (!selectedOption || selectedOption.result) break;
        currentId = selectedOption.next;
      } else {
        // For future un-answered questions, optimistically follow the first option
        // to estimate a typical remaining path length.
        const firstOption = question.options?.[0];
        if (!firstOption || firstOption.result) break;
        currentId = firstOption.next;
      }

      steps++;
    }

    return steps;
  }

  /* -------------------------------------------------- */
  /*  Reset                                              */
  /* -------------------------------------------------- */
  resetQuiz() {
    // Clear all collected state.
    this.currentQuestionId = 'q1';
    this.answers           = {};
    this.history           = [];
    this.selectedOption    = null;
    this._currentProduct   = null;
    this._currentResult    = null;

    // Undo result-screen DOM mutations.
    this.quizContent.classList.remove('quiz-content--result');

    // Restore navigation bar if showResult() detached it.
    this.navigation.style.display = '';

    this.renderQuestion(this.currentQuestionId);

    // Let any parent layer (analytics, A/B tests) react to the restart.
    this.dispatchEvent(new CustomEvent('quiz:reset', { bubbles: true }));
  }

} // ← class closes here

// Register only once — safe for HMR and Shopify's section reloads.
if (!customElements.get('quiz-container')) {
  customElements.define('quiz-container', QuizContainer);
}